import * as d3 from 'd3';

import type {
  CodingResultsSubtopic,
  CodingResultSubTopicTotal,
} from 'src/types/dashboard';
import {
  ARC_EXTENSION_LENGTH,
  CLASS_NAME,
  CLASS_NAME_TARGET,
  FONT_SIZE,
  GROUP_PAD_ANGLE,
  LINE_HEIGHT,
  MARKER_HEIGHT,
  MARKER_RADIUS,
  RIBBON_OPACITY,
  WHEEL_RADIUS,
} from 'src/components/dashboard/DashboardPage/widgets/InsightWheelWidget/InsightWheel/constants';
import type { LabelPositions } from 'src/components/dashboard/DashboardPage/widgets/InsightWheelWidget/InsightWheel/helper';
import {
  arcRadiusDifference,
  d3SelectionAppendClassTo,
  getChartDimensions,
  getLabelPositions,
  linkageMatrix,
  shiftLabelsEvenQuadrants,
  shiftLabelsOddQuadrants,
} from 'src/components/dashboard/DashboardPage/widgets/InsightWheelWidget/InsightWheel/helper';

type D3Svg = d3.Selection<SVGSVGElement, unknown, null, undefined>;

const drawArcs = (
  svg: D3Svg,
  chordDiagram: d3.Chords,
  subTopics: CodingResultsSubtopic[] | CodingResultSubTopicTotal[],
) => {
  const arc: d3.Arc<any, any> = d3
    .arc()
    .startAngle((d) => d.startAngle - ARC_EXTENSION_LENGTH)
    .endAngle((d) => d.endAngle + ARC_EXTENSION_LENGTH)
    .innerRadius(
      (d) => WHEEL_RADIUS - arcRadiusDifference(d.startAngle, d.endAngle),
    )
    .outerRadius(
      (d) => WHEEL_RADIUS + arcRadiusDifference(d.startAngle, d.endAngle),
    )
    .cornerRadius((d) => arcRadiusDifference(d.startAngle, d.endAngle));

  return svg
    .datum(chordDiagram)
    .append('g')
    .selectAll('g')
    .data((d) => d.groups)
    .enter()
    .append('path')
    .style('fill', (_, i) => subTopics[i].color)
    .style('stroke', (_, i) => subTopics[i].color)
    .attr('d', arc)
    .attr('class', (_, index) => `${CLASS_NAME}-${index}`);
};

type GroupSVGElement = d3.Selection<
  SVGGElement,
  [number, number],
  SVGGElement,
  LabelPositions
>;

const drawTextBackground = (
  group: GroupSVGElement,
  subTopics: CodingResultsSubtopic[] | CodingResultSubTopicTotal[],
) =>
  group
    .append('rect')
    .attr('rx', MARKER_RADIUS)
    .attr('ry', MARKER_RADIUS)
    .attr('height', MARKER_HEIGHT)
    .style('fill', (_, i) => subTopics[i].color)
    .style('opacity', 0.2)
    .attr('y', ([_, y]) => y - LINE_HEIGHT * 0.5);

const drawColorMarker = (
  group: GroupSVGElement,
  subTopics: CodingResultsSubtopic[] | CodingResultSubTopicTotal[],
  r = 4,
) =>
  group
    .append('path')
    .style('fill', (_, i) => subTopics[i].color)
    // create a path: M = Move to x,y; h = horizontal line; v = vertical line; q = quadratic curve
    .attr('d', ([x, y]) =>
      x < 0
        ? `M${x - 12},${y - 10} h2 q${r},0 ${r},${r} v${20 - 2 * r} q0,${r} -${r},${r} h-2 v20`
        : `M${x + 2},${y - 10} h2 v20 h-2 q-${r},0 -${r},-${r} v-${20 - 2 * r} q0,-${r} ${r},-${r}`,
    );

const drawTextBox = (
  group: GroupSVGElement,
  subTopics: CodingResultsSubtopic[] | CodingResultSubTopicTotal[],
) => {
  const textBox = group
    .append('text')
    .text((d, i) => {
      const label = subTopics[i].topicLabel.trim();
      if (label.length > 20) {
        return `${label.slice(0, 20).trimEnd()}...`;
      }
      return label;
    })
    .attr('font-size', FONT_SIZE)
    .attr('x', ([x, _]) => (x > 0 ? x + 10 : x - 20))
    .attr('y', ([_, y]) => y + FONT_SIZE / 3)
    .attr('text-anchor', ([x]) => (x > 0 ? 'start' : 'end'))
    .attr('cursor', 'default');
  textBox.append('title').text((_, i) => subTopics[i].topicLabel);
  return textBox;
};

const drawLabels = (
  svg: D3Svg,
  labelPositions: LabelPositions,
  subTopics: CodingResultsSubtopic[] | CodingResultSubTopicTotal[],
) => {
  const group = svg
    .datum(labelPositions)
    .append('g')
    .selectAll('g')
    .data((d) => d)
    .enter()
    .append('g')
    .attr('class', (_, index) => `${CLASS_NAME}-${index}`);

  const textBackground = drawTextBackground(group, subTopics);
  drawColorMarker(group, subTopics);
  const textBox = drawTextBox(group, subTopics);

  // calculate text box width and adjust background box width accordingly
  const textWidths = textBox
    .nodes()
    .map((node) => (node && node.getBBox ? node.getBBox().width : 180));
  textBackground
    .attr('width', (_, i) => Number(textWidths[i] + 20))
    .attr('x', ([x, _], i) => {
      const width = textWidths[i];
      return x > 0 ? x : x - width - 28;
    });

  return group;
};

const drawRibbons = (
  svg: D3Svg,
  chordDiagram: d3.Chords,
  subTopics: CodingResultsSubtopic[] | CodingResultSubTopicTotal[],
) => {
  const ribbonGenerator = d3.ribbon().radius(WHEEL_RADIUS);
  return svg
    .datum(chordDiagram)
    .append('g')
    .selectAll('path')
    .data((d) => d)
    .enter()
    .append('path')
    .attr('d', ribbonGenerator as any)
    .attr('fill-opacity', RIBBON_OPACITY)
    .style('fill', ({ source, target }) => {
      if (subTopics[source.index].count > subTopics[target.index].count) {
        return subTopics[source.index].color;
      }
      return subTopics[target.index].color;
    });
};

export const renderInsightWheel = (
  container: HTMLDivElement | null,
  subTopics: CodingResultsSubtopic[] | CodingResultSubTopicTotal[],
) => {
  const chordDiagram = d3.chord().padAngle(GROUP_PAD_ANGLE)(
    linkageMatrix(subTopics),
  );
  const chordGroups = chordDiagram.groups;
  const labelPositions: LabelPositions = getLabelPositions(chordGroups);

  shiftLabelsEvenQuadrants(labelPositions);
  shiftLabelsOddQuadrants(labelPositions);

  const [wheelWidth, wheelHeight] = getChartDimensions(labelPositions);
  const svg = d3
    .select(container)
    .html('')
    .append('svg')
    .attr('width', wheelWidth)
    .attr('height', wheelHeight)
    .attr('viewBox', [
      -wheelWidth / 2,
      -wheelHeight / 2,
      wheelWidth,
      wheelHeight,
    ]);

  const ribbons = drawRibbons(svg, chordDiagram, subTopics);
  // draw arcs and labels after the ribbons so that the ribbons don't overlap the arcs
  const arcs = drawArcs(svg, chordDiagram, subTopics);
  const labels = drawLabels(svg, labelPositions, subTopics);
  ribbons.attr('class', ({ source, target }) => {
    // the target and source class allow for an element to be associated
    // with a data point and therefore stay highlighted on hover
    const targetToSourceClass = `${CLASS_NAME_TARGET}-${source.index}`;
    const sourceToTargetClass = `${CLASS_NAME_TARGET}-${target.index}`;
    d3SelectionAppendClassTo(arcs, target.index, targetToSourceClass);
    d3SelectionAppendClassTo(arcs, source.index, sourceToTargetClass);
    d3SelectionAppendClassTo(labels, target.index, targetToSourceClass);
    d3SelectionAppendClassTo(labels, source.index, sourceToTargetClass);
    return `${CLASS_NAME}-${source.index} ${targetToSourceClass} ${CLASS_NAME}-${target.index} ${sourceToTargetClass}`;
  });

  return svg;
};

export default renderInsightWheel;
